import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, ArrowRight, Users, Calendar, Search, SlidersHorizontal, ChevronDown, ExternalLink, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import api from '../services/api';
import { FlightCard, FlightFilters, PopularRoutes, FlightCompensation, TravelServices } from '../components/flights/FlightComponents';

export default function FlightSearchPage() {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [adults, setAdults] = useState(1);
  const [tripType, setTripType] = useState('one_way');
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [sortBy, setSortBy] = useState('price_asc');
  const [filterStops, setFilterStops] = useState('any');
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get('/flights/popular-routes').then(r => setPopularRoutes(r.data.routes || [])).catch(() => {});
  }, []);

  const handleSearch = async (e, provider = 'expedia') => {
    if (e) e.preventDefault();
    if (!origin || !destination || !departureDate) return;

    if (provider === 'aviasales') {
      window.open('https://aviasales.tpm.lv/3ogiO1hK', '_blank', 'noopener,noreferrer');
      return;
    }

    let expediaUrl = `https://www.expedia.com/Flights-Search?leg1=from:${encodeURIComponent(origin)},to:${encodeURIComponent(destination)},departure:${departureDate}TANYT&passengers=adults:${adults},children:0,infantinlap:N&options=cabinClass:coach&mode=search&pageId=0`;
    if (tripType === 'round_trip' && returnDate) {
      expediaUrl += `&leg2=from:${encodeURIComponent(destination)},to:${encodeURIComponent(origin)},departure:${returnDate}TANYT&trip=roundtrip`;
    } else {
      expediaUrl += '&trip=oneway';
    }
    window.open(expediaUrl, '_blank', 'noopener,noreferrer');
  };

  const sortedFlights = useMemo(() => {
    let list = [...flights];
    if (filterStops !== 'any') {
      const maxStops = filterStops === 'nonstop' ? 0 : filterStops === '1stop' ? 1 : 2;
      list = list.filter(f => f.stops <= maxStops);
    }
    switch (sortBy) {
      case 'price_asc': list.sort((a, b) => a.price - b.price); break;
      case 'price_desc': list.sort((a, b) => b.price - a.price); break;
      case 'duration': list.sort((a, b) => a.duration_minutes - b.duration_minutes); break;
      case 'departure': list.sort((a, b) => a.departure_time.localeCompare(b.departure_time)); break;
      default: break;
    }
    return list;
  }, [flights, sortBy, filterStops]);

  const handlePopularRoute = (route) => {
    setOrigin(route.origin);
    setDestination(route.destination);
    const d = new Date(); d.setDate(d.getDate() + 14);
    setDepartureDate(d.toISOString().split('T')[0]);
    setTripType('one_way');
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="flight-search-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Dashboard
            </button>
          </div>
          <div className="text-center mb-8" data-testid="flight-hero">
            <div className="w-14 h-14 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Plane className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Find Your Perfect Flight</h1>
            <p className="text-white/80">Compare prices across 750+ airlines worldwide</p>
          </div>

          {/* Search Form */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-6 md:p-8 mb-8" data-testid="flight-search-form">
            <form onSubmit={handleSearch}>
              <div className="flex gap-2 mb-5">
                <button type="button" onClick={() => setTripType('one_way')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${tripType === 'one_way' ? 'bg-gradient-to-r from-ocean to-sky text-white shadow-md' : 'bg-ocean/10 text-ocean/70 hover:bg-ocean/20'}`}
                  data-testid="trip-type-oneway">One Way</button>
                <button type="button" onClick={() => setTripType('round_trip')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${tripType === 'round_trip' ? 'bg-gradient-to-r from-ocean to-sky text-white shadow-md' : 'bg-ocean/10 text-ocean/70 hover:bg-ocean/20'}`}
                  data-testid="trip-type-roundtrip">Round Trip</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">From</label>
                  <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                    <Plane className="w-4 h-4 text-ocean/40 mr-2 rotate-45" />
                    <input type="text" value={origin} onChange={e => setOrigin(e.target.value)} placeholder="City or Airport"
                      className="bg-transparent text-ocean placeholder:text-ocean/30 outline-none w-full text-sm"
                      data-testid="flight-search-input-origin" />
                  </div>
                </div>
                <div>
                  <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">To</label>
                  <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                    <Plane className="w-4 h-4 text-ocean/40 mr-2 -rotate-45" />
                    <input type="text" value={destination} onChange={e => setDestination(e.target.value)} placeholder="City or Airport"
                      className="bg-transparent text-ocean placeholder:text-ocean/30 outline-none w-full text-sm"
                      data-testid="flight-search-input-destination" />
                  </div>
                </div>
                <div>
                  <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Departure</label>
                  <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                    <Calendar className="w-4 h-4 text-ocean/40 mr-2" />
                    <input type="date" value={departureDate} onChange={e => setDepartureDate(e.target.value)}
                      className="bg-transparent text-ocean outline-none w-full text-sm"
                      data-testid="flight-search-input-departure" />
                  </div>
                </div>
                {tripType === 'round_trip' ? (
                  <div>
                    <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Return</label>
                    <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                      <Calendar className="w-4 h-4 text-ocean/40 mr-2" />
                      <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)}
                        className="bg-transparent text-ocean outline-none w-full text-sm"
                        data-testid="flight-search-input-return" />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Passengers</label>
                    <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                      <Users className="w-4 h-4 text-ocean/40 mr-2" />
                      <select value={adults} onChange={e => setAdults(Number(e.target.value))}
                        className="bg-transparent text-ocean outline-none w-full text-sm appearance-none cursor-pointer"
                        data-testid="flight-search-input-passengers">
                        {[1,2,3,4,5,6,7,8,9].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Adult' : 'Adults'}</option>)}
                      </select>
                      <ChevronDown className="w-4 h-4 text-ocean/40" />
                    </div>
                  </div>
                )}
              </div>

              {tripType === 'round_trip' && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="text-ocean/70 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Passengers</label>
                    <div className="flex items-center border border-ocean/20 rounded-xl px-4 py-3 focus-within:border-ocean transition-colors bg-ocean/5">
                      <Users className="w-4 h-4 text-ocean/40 mr-2" />
                      <select value={adults} onChange={e => setAdults(Number(e.target.value))}
                        className="bg-transparent text-ocean outline-none w-full text-sm appearance-none cursor-pointer"
                        data-testid="flight-search-passengers-roundtrip">
                        {[1,2,3,4,5,6,7,8,9].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Adult' : 'Adults'}</option>)}
                      </select>
                      <ChevronDown className="w-4 h-4 text-ocean/40" />
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button onClick={(e) => handleSearch(e, 'expedia')} disabled={!origin || !destination || !departureDate}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold rounded-full px-8 py-3 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 text-sm w-full sm:w-auto"
                  data-testid="flight-search-expedia">
                  <Search className="w-4 h-4 mr-2" /> Search on Expedia <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </Button>
                <Button onClick={() => window.open('https://travelean.com', '_blank', 'noopener,noreferrer')} disabled={!origin || !destination || !departureDate}
                  className="bg-gradient-to-r from-sky-600 to-sky-700 text-white font-semibold rounded-full px-8 py-3 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 text-sm w-full sm:w-auto"
                  data-testid="flight-search-travelean">
                  <Search className="w-4 h-4 mr-2" /> Search on Travelean <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </Button>
                <Button onClick={(e) => handleSearch(e, 'aviasales')} disabled={!origin || !destination || !departureDate}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-full px-8 py-3 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 text-sm w-full sm:w-auto"
                  data-testid="flight-search-aviasales">
                  <Search className="w-4 h-4 mr-2" /> Search on Aviasales <ExternalLink className="w-3.5 h-3.5 ml-2" />
                </Button>
              </div>
            </form>
          </div>

          {/* Content Area */}
          {searched ? (
            <>
              {isDemo && flights.length > 0 && (
                <div className="bg-gold/10 border border-gold/30 rounded-xl px-4 py-3 mb-6 flex items-center gap-3" data-testid="demo-notice">
                  <AlertCircle className="w-4 h-4 text-gold shrink-0" />
                  <p className="text-white/90 text-sm">Showing sample flight results. Connect your Kiwi.com API key for live prices.</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <h2 className="font-serif text-2xl text-white drop-shadow-lg">
                  {sortedFlights.length} {sortedFlights.length === 1 ? 'Flight' : 'Flights'} Found
                </h2>
                <button onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white text-sm border border-white/20 hover:bg-white/30 transition-colors"
                  data-testid="toggle-filters">
                  <SlidersHorizontal className="w-4 h-4" /> Filters
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className={`lg:col-span-3 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                  <FlightFilters sortBy={sortBy} setSortBy={setSortBy} filterStops={filterStops}
                    setFilterStops={setFilterStops} onClose={() => setShowFilters(false)} />
                </div>

                <div className="lg:col-span-9 space-y-4" data-testid="flight-results">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                      <Loader2 className="w-8 h-8 text-gold animate-spin" />
                      <p className="text-white/70 mt-4 text-sm">Searching flights...</p>
                    </div>
                  ) : sortedFlights.length === 0 ? (
                    <div className="text-center py-20">
                      <Plane className="w-12 h-12 text-white/30 mx-auto mb-4" />
                      <p className="text-white/70 text-lg">No flights found. Try different dates or destinations.</p>
                    </div>
                  ) : (
                    sortedFlights.map((flight, idx) => (
                      <FlightCard key={flight.id || idx} flight={flight} />
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <PopularRoutes routes={popularRoutes} onSelect={handlePopularRoute} />
          )}

          {/* Travel Services Section */}
          <div className="mt-12 space-y-6">
            <FlightCompensation />
            <TravelServices />
          </div>

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 py-8 px-4 text-center relative z-10">
        <p className="text-white/40 text-xs">Powered by Expedia & Aviasales &middot; Triple A Travel Solutions</p>
      </footer>
    </div>
  );
}
